import { cloneElement, Fragment, lazy, Suspense } from 'react';
import { useContextTranslation } from '@core/i18n';
const TransComponent = lazy(() => import('react-i18next').then((module) => ({ default: module.Trans })));
/**
 * Lazy <Trans /> component to handle translations which require html/jsx passing in.
 *
 * Note 1: All the props are passed to the `Trans` component from `react-i18next` in case
 * the language requested is not default (key).
 * Note 2: Do not use `children` prop as it will be ignored.
 * Note 3: Successful extraction requires the `i18nKey` and `defaults` prop to be passed in.
 * Note 4: Successful extraction requires the import from `@core/react-i18next-lazy` to be used.
 *
 * @example
 * ```tsx
 * import { Trans } from '@core/react-i18next-lazy'
 *
 * <Trans
 *   ns="translation"
 *   i18nKey="Hello <0>{{what}}</0>"
 *   defaults="Hello <0>{{what}}</0>"
 *   values={{ what: 'World' }}
 *   components={[<strong key="1" />Universe</strong>]}
 * />
 * ```
 *
 * @param props
 */
export function Trans(props) {
    const { i18nKey, count, values, components } = props;
    const { t, i18n } = useContextTranslation();
    if (i18n) {
        return (<Suspense fallback={<Fragment />}>
        <TransComponent {...props}/>
      </Suspense>);
    }
    /* i18next-extract-disable-next-line */
    const translation = t(i18nKey || '', {
        count,
        ...values,
    });
    const parseNestedTags = (text) => {
        const elements = [];
        const regex = /<(\d+)>(.*?)<\/\1>/g;
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(text)) !== null) {
            const [_, tagIndex, content] = match;
            const startIndex = match.index;
            const endIndex = regex.lastIndex;
            // Push the text before the current match
            if (startIndex > lastIndex) {
                elements.push(text.slice(lastIndex, startIndex));
            }
            // Recursively parse the content inside the current tag
            const parsedContent = parseNestedTags(content);
            // Create the JSX element for the current tag
            const componentIndex = parseInt(tagIndex, 10);
            const Component = components?.[componentIndex] || <Fragment></Fragment>;
            if (Component) {
                elements.push(cloneElement(Component, { key: elements.length }, parsedContent));
            }
            lastIndex = endIndex;
        }
        // Push the remaining text after the last match
        if (lastIndex < text.length) {
            elements.push(text.slice(lastIndex));
        }
        return elements;
    };
    return parseNestedTags(`${translation}`);
}
